import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

// Admin overview dashboard (read-only): AI usage/cost, subscriptions/revenue,
// system health, signals/strategy, watchlist analytics, signup funnel.

const Stat = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-lg shadow-sm p-4">
    <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value ?? '—'}</div>
    <div className="text-sm text-gray-500">{label}</div>
    {sub != null && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-sm">
    <div className="px-4 py-2 border-b border-gray-100 font-semibold text-gray-900">{title}</div>
    <div className="p-4">{children}</div>
  </div>
);

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

const AdminDashboard = ({ embedded = false }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/dashboard');
      setD(res.data || {});
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subs = d?.subscriptions || {};
  const ai = d?.ai || {};
  const chat = ai.chat30d || {};
  const analysis = ai.analysis || {};
  const health = d?.health || {};
  const strat = d?.strategy || {};
  const wl = d?.watchlist || {};
  const funnel = d?.funnel || {};

  const inner = (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>}
          <p className="text-sm text-gray-500">Operational overview (read-only)</p>
        </div>
        <div className="flex items-center gap-2">
          {!embedded && <Link to="/admin" className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Users</Link>}
          {!embedded && <Link to="/admin/chat" className="px-3 py-1.5 text-sm text-purple-700 border border-purple-200 rounded hover:bg-purple-50">AI Chat</Link>}
          <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500">Loading…</div>
      ) : (
          <div className="space-y-6">
            {/* Revenue / subscriptions */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Revenue &amp; Members</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <Stat label="MRR" value={money(subs.mrrUsd)} color="text-green-600" />
                <Stat label="ARR" value={money(subs.arrUsd)} color="text-green-600" />
                <Stat label="Premium" value={subs.premium} color="text-indigo-600" />
                <Stat label="Free" value={subs.free} />
                <Stat label="Total members" value={subs.total_members} />
                <Stat label="New (30d)" value={subs.new_30d} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Stat label="Verified" value={subs.verified} />
                <Stat label="Renewing (14d)" value={subs.renewing_14d} color="text-amber-600" />
                <Stat label="Signups (7d)" value={funnel.signups_7d} />
                <Stat label="Converted → Premium" value={funnel.converted_premium} color="text-indigo-600" />
              </div>
            </div>

            {/* AI usage / cost */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">AI Usage &amp; Cost</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <Stat label="Chat questions (30d)" value={chat.questions} />
                <Stat label="Chat tokens (30d)" value={chat.tokens != null ? Number(chat.tokens).toLocaleString() : '—'} />
                <Stat label="Est. chat cost (30d)" value={money(ai.estChatCost30dUsd)} color="text-amber-600" sub="approx, blended" />
                <Stat label="AI analyses (30d)" value={analysis.last30d} />
                <Stat label="AI analyses (24h)" value={analysis.last24h} />
              </div>
              {Array.isArray(ai.verdictBreakdown) && ai.verdictBreakdown.length > 0 && (
                <div className="mt-3">
                  <Card title="AI verdict breakdown">
                    <div className="flex flex-wrap gap-3 text-sm">
                      {ai.verdictBreakdown.map((v) => (
                        <span key={v.verdict} className="px-2 py-1 bg-gray-100 rounded">{v.verdict}: <b>{v.n}</b></span>
                      ))}
                    </div>
                  </Card>
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Health */}
              <Card title="System Health">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">DB</span><span className="text-green-600 font-medium">{health.dbOk ? 'OK' : 'DOWN'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Last digest</span><span>{health.lastDigest ? `${health.lastDigest.digest_date} (${health.lastDigest.signal_count} signals)` : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Last signal date</span><span>{health.signals?.last_signal_date || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Last signal generated</span><span>{fmt(health.signals?.last_signal_created)}</span></div>
                </div>
              </Card>

              {/* Strategy */}
              <Card title="Strategy Monitor">
                <div className="text-sm space-y-1 mb-3">
                  <div className="flex justify-between"><span className="text-gray-500">Latest sector pick</span><span className="font-medium">{strat.latestSectorPick ? `${strat.latestSectorPick.symbol} (${strat.latestSectorPick.signaldate})` : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Latest regime</span><span>{strat.latestRegime ? `${strat.latestRegime.signaltype} (${strat.latestRegime.signaldate})` : '—'}</span></div>
                </div>
                <div className="text-xs text-gray-500 mb-1">Recent signals</div>
                <div className="max-h-40 overflow-y-auto text-xs">
                  {(strat.recentSignals || []).map((s, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-50 py-1">
                      <span className="font-medium">{s.symbol}</span>
                      <span className="text-gray-500">{s.signaltype} · {s.interval} · {s.signaldate}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Watchlist analytics */}
            <Card title="Watchlist Analytics">
              <div className="flex gap-6 mb-3 text-sm">
                <span>Entries: <b>{wl.totals?.entries ?? '—'}</b></span>
                <span>Members tracking: <b>{wl.totals?.members ?? '—'}</b></span>
              </div>
              <div className="text-xs text-gray-500 mb-1">Most-watchlisted symbols</div>
              <div className="flex flex-wrap gap-2 text-sm">
                {(wl.topSymbols || []).map((t) => (
                  <span key={t.symbol} className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{t.symbol} <b>×{t.watchers}</b></span>
                ))}
                {(!wl.topSymbols || wl.topSymbols.length === 0) && <span className="text-gray-400">No watchlist data yet.</span>}
              </div>
            </Card>

            <p className="text-xs text-gray-400">
              Cost figures are rough estimates (blended token pricing), not billed amounts — check AWS Billing for actuals.
            </p>
          </div>
        )}
    </>
  );

  // Embedded in the Admin hub (tab): render inner only. Standalone: wrap in the page shell.
  if (embedded) return inner;
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">{inner}</div>
    </div>
  );
};

export default AdminDashboard;
